import { DoctorGroup } from '../detail/doctorInfo/groupCandidatesByDoctor';
import { ApsCandidate } from '../detail/doctorInfo/types';
import { PROFESSIONAL_SUFFIXES, US_STATES } from './constants';
import { Doctor, DoctorFormData } from './types';

export const apsGroupToDoctor = (g: DoctorGroup): Doctor => ({
  id: g.key,
  source: g.source,
  information: g.information,
  providerNPI: g.npi,
  name: g.name,
  phoneNumber: g.phoneNumber,
  address: g.address,
  city: g.city,
  state: g.state,
  zipCode: g.zipcode,
  facilityName: g.facilityName || undefined,
});

export const previouslyOrderedToApsCandidate = (
  d: Doctor,
  index: number,
): ApsCandidate => ({
  key: `previously-ordered-${d.doctorUuid ?? d.id}-${index}`,
  source: 'PreviouslyOrdered',
  information: d.information ?? '',
  specialization: '',
  code: '',
  description: '',
  name: d.name ?? '',
  npi: d.providerNPI ?? '',
  phoneNumber: d.phoneNumber ?? '',
  address: d.address ?? '',
  city: d.city ?? '',
  state: d.state ?? '',
  zipcode: d.zipCode ?? '',
  facilityName: d.facilityName ?? '',
  visitDate: '',
});

// Regex pattern for extracting doctor names from facility names
// Handles: hyphens (Mary-Jane), apostrophes (O'Brien), multiple capitals (McDonald, DeVito), commas (Smith, John)
// Supports single names (O'Brien) or multiple names (John Smith)
const DOCTOR_NAME_IN_FACILITY_REGEX =
  /(?:^|\s)(?:Dr\.?\s+)?([A-Z][a-z]*(?:[A-Z][a-z]*)*(?:['-][A-Z]?[a-z]+)*(?:,\s*[A-Z][a-z]*(?:[A-Z][a-z]*)*(?:['-][A-Z]?[a-z]+)*)?(?:\s+[A-Z][a-z]*(?:[A-Z][a-z]*)*(?:['-][A-Z]?[a-z]+)*)*)(?:\s+at|\s+@)/i;

/**
 * Detects if a name is a facility (hospital, clinic, etc.) rather than a person
 */
const isFacilityName = (name: string): boolean => {
  if (!name) return false;

  const facilityKeywords = [
    'hospital',
    'clinic',
    'medical',
    'medical center',
    'health center',
    'healthcare',
    'health care',
    'urgent care',
    'emergency',
    'surgery center',
    'surgical center',
    'specialty',
    'specialties',
    'group',
    'associates',
    'physicians',
    'physician group',
    'practice',
    'medical group',
    'health system',
    'healthcare system',
  ];

  const lowerName = name.toLowerCase();
  return facilityKeywords.some(keyword => lowerName.includes(keyword));
};

/**
 * Maps a Doctor object to DoctorFormData format
 * Handles both Google Places results and saved doctors
 */
export const mapDoctorToFormData = (doctor: Doctor): DoctorFormData => {
  let doctorName = doctor.name?.trim() || '';
  const isFacility = isFacilityName(doctorName);

  let firstName = '';
  let lastName = '';
  let facilityName: string | undefined = undefined;

  if (isFacility) {
    facilityName = doctorName;

    const doctorNameMatch = doctorName.match(DOCTOR_NAME_IN_FACILITY_REGEX);
    if (doctorNameMatch) {
      const extractedName = doctorNameMatch[1].trim();
      // Handle "Last, First" format or "First Last" format
      if (extractedName.includes(',')) {
        const parts = extractedName
          .split(',')
          .map(part => part.trim())
          .filter(part => part);
        lastName = parts[0] || '';
        firstName = parts.length > 1 ? parts.slice(1).join(' ') : '';
      } else {
        const nameParts = extractedName
          .split(/\s+/)
          .filter(part => part.trim() !== '');
        firstName = nameParts[0] || '';
        lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
      }
    }
  } else {
    doctorName = doctorName.replace(/^Dr\.?\s+/i, '').trim();

    const suffixPattern = PROFESSIONAL_SUFFIXES.join('|');
    const suffixRegex = new RegExp(`^(.+?),\\s*(${suffixPattern})$`, 'i');
    const suffixMatch = doctorName.match(suffixRegex);
    if (suffixMatch) {
      doctorName = suffixMatch[1].trim();
    }

    const nameParts = doctorName
      .split(/\s+/)
      .filter(part => part.trim() !== '');
    firstName = nameParts[0] || '';
    lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
  }

  let stateAbbr = doctor.state || '';
  if (stateAbbr && stateAbbr.length > 2) {
    const stateEntry = US_STATES.find(
      s => s.label.toLowerCase() === stateAbbr.toLowerCase(),
    );
    if (stateEntry) {
      stateAbbr = stateEntry.value;
    }
  }

  return {
    doctorUuid:
      doctor.doctorUuid ||
      (typeof doctor.id === 'string' &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        doctor.id,
      )
        ? doctor.id
        : undefined),
    firstName,
    lastName,
    facilityName: facilityName || doctor.facilityName || undefined,
    phoneNumber: doctor.phoneNumber || '',
    streetAddress: doctor.address,
    city: doctor.city,
    state: stateAbbr,
    zipCode: doctor.zipCode || '',
  };
};
